import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, 
  ChevronRight, 
  Check, 
  X, 
  Edit3, 
  RotateCcw, 
  Eye, 
  Sparkles,
  Lock,
  Filter,
  Briefcase,
  FileText,
  CreditCard,
  DollarSign,
  Users,
  Settings,
  AlertCircle,
  CheckCircle2,
  Copy
} from 'lucide-react';
import api from '../services/api';
import { useBusinessContext } from '../contexts/BusinessContext';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';

// Category icons and colors
const categoryConfig = {
  Jobs: { icon: Briefcase, color: 'bg-blue-100 text-blue-600', borderColor: 'border-blue-200' },
  Invoices: { icon: FileText, color: 'bg-amber-100 text-amber-600', borderColor: 'border-amber-200' },
  Payments: { icon: DollarSign, color: 'bg-green-100 text-green-600', borderColor: 'border-green-200' },
  Marketing: { icon: Users, color: 'bg-purple-100 text-purple-600', borderColor: 'border-purple-200' },
  System: { icon: Settings, color: 'bg-gray-100 text-gray-600', borderColor: 'border-gray-200' },
  Other: { icon: Mail, color: 'bg-gray-100 text-gray-600', borderColor: 'border-gray-200' },
};

const EmailTemplatesPage = () => {
  const navigate = useNavigate();
  const { user } = useBusinessContext();
  
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'custom'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({ subject: '', htmlBody: '', isActive: true });
  
  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger',
    confirmText: '',
  });
  
  // Subscription info
  const [subscriptionPlan, setSubscriptionPlan] = useState('Solo');
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [canCustomize, setCanCustomize] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/emailtemplates');
      
      // The API returns { templates, grouped, subscriptionPlan, isTrialActive, canCustomize }
      const data = response.data;
      
      setTemplates(data.templates || []);
      setSubscriptionPlan(data.subscriptionPlan || 'Solo');
      setIsTrialActive(data.isTrialActive || false);
      setCanCustomize(data.canCustomize || false);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories from templates
  const categories = ['all', ...new Set(templates.map(t => t.category || 'Other'))];
  
  // Filter templates based on view mode and category
  const filteredTemplates = templates.filter(t => {
    // View mode filter
    // "all" = System defaults only (NOT customized)
    // "custom" = Business overrides only (IS customized)
    if (viewMode === 'all' && t.isCustomized) return false;
    if (viewMode === 'custom' && !t.isCustomized) return false;
    
    // Category filter
    if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
    
    return true;
  });

  // Count custom templates
  const customCount = templates.filter(t => t.isCustomized).length;

  const handlePreview = async (template) => {
    setSelectedTemplate(template);
    try {
      const response = await api.post('/emailtemplates/preview', {
        subject: template.subject,
        htmlBody: template.htmlBody,
        plainTextBody: template.plainTextBody
      });
      setPreviewData(response.data);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      // Still show modal with raw template
      setPreviewData({
        subject: template.subject,
        htmlBody: template.htmlBody,
        sampleData: {}
      });
      setShowPreviewModal(true);
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setEditForm({
      subject: template.subject,
      htmlBody: template.htmlBody,
      isActive: template.isActive
    });
    setShowEditModal(true);
  };

  const handleCustomize = (template) => {
    // Start customizing from system default
    setSelectedTemplate(template);
    setEditForm({
      subject: template.subject,
      htmlBody: template.htmlBody,
      isActive: template.isActive
    });
    setShowEditModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      await api.post('/emailtemplates/customize', {
        templateType: selectedTemplate.templateType,
        subject: editForm.subject,
        htmlBody: editForm.htmlBody,
        isActive: editForm.isActive
      });
      
      await loadTemplates();
      setShowEditModal(false);
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Error saving template:', error);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to save template. Please try again.',
        variant: 'danger',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResetTemplate = async (template) => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset to Default',
      message: `Reset "${template.displayName}" to the system default? Your customizations will be permanently deleted.`,
      variant: 'danger',
      confirmText: 'Reset',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
          await api.post(`/emailtemplates/reset/${template.templateType}`);
          await loadTemplates();
        } catch (error) {
          console.error('Error resetting template:', error);
          setConfirmModal({
            isOpen: true,
            title: 'Error',
            message: 'Failed to reset template. Please try again.',
            variant: 'danger',
            confirmText: 'OK',
            onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
          });
        }
      },
    });
  };

  const handleToggleActive = async (template) => {
    try {
      await api.post(`/emailtemplates/toggle/${template.templateType}`, {
        isActive: !template.isActive
      });
      await loadTemplates();
    } catch (error) {
      console.error('Error toggling template:', error);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to update template status. Please try again.',
        variant: 'danger',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
      });
    }
  };

  const getCategoryIcon = (category) => {
    const config = categoryConfig[category] || categoryConfig.Other;
    const IconComponent = config.icon;
    return <IconComponent size={18} />;
  };

  const getTierBadge = (requiredTier) => {
    const colors = {
      Solo: 'bg-gray-100 text-gray-700',
      Pro: 'bg-blue-100 text-blue-700',
      Enterprise: 'bg-purple-100 text-purple-700'
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[requiredTier] || colors.Solo}`}>
        {requiredTier}
      </span>
    );
  };

  if (loading) return <Loading fullScreen />;

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Email Templates</h1>
          <p className="text-gray-600">
            Manage automated emails sent to your customers
          </p>
        </div>

        {/* Subscription Notice */}
        {!canCustomize && (
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Lock className="text-purple-600 mt-0.5" size={20} />
              <div>
                <h3 className="font-semibold text-purple-900">Template Customization</h3>
                <p className="text-sm text-purple-800 mt-1">
                  You're on the <strong>{subscriptionPlan}</strong> plan. 
                  Upgrade to <strong>Pro</strong> or <strong>Enterprise</strong> to customize email templates.
                  Your emails currently use the professional system defaults.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* View Toggle + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('all')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === 'all'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              System Templates
              {templates.filter(t => !t.isCustomized).length > 0 && (
                <span className="ml-2 text-xs text-gray-500">
                  ({templates.filter(t => !t.isCustomized).length})
                </span>
              )}
            </button>
            <button
              onClick={() => setViewMode('custom')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                viewMode === 'custom'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Sparkles size={14} />
              My Custom
              {customCount > 0 && (
                <span className="bg-primary-100 text-primary-700 text-xs px-1.5 py-0.5 rounded-full">
                  {customCount}
                </span>
              )}
            </button>
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <Filter size={16} className="text-gray-400 flex-shrink-0" />
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  categoryFilter === cat
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat === 'all' ? 'All Categories' : cat}
              </button>
            ))}
          </div>
        </div>

        {/* Empty State for Custom View */}
        {viewMode === 'custom' && filteredTemplates.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="text-gray-400" size={28} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Custom Templates Yet</h3>
            <p className="text-gray-600 mb-4">
              You haven't customized any templates. Your emails are using the professional system defaults.
            </p>
            <button
              onClick={() => setViewMode('all')}
              className="btn btn-primary"
            >
              Browse System Templates
            </button>
          </div>
        )}

        {/* Empty State for System View (all customized) */}
        {viewMode === 'all' && filteredTemplates.length === 0 && templates.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="text-primary-600" size={28} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">All Templates Customized!</h3>
            <p className="text-gray-600 mb-4">
              You've created custom versions of all available templates. View them in "My Custom".
            </p>
            <button
              onClick={() => setViewMode('custom')}
              className="btn btn-primary"
            >
              View My Custom Templates
            </button>
          </div>
        )}

        {/* Templates List */}
        {filteredTemplates.length > 0 && (
          <div className="space-y-3">
            {filteredTemplates.map((template) => {
              const catConfig = categoryConfig[template.category] || categoryConfig.Other;
              
              return (
                <div
                  key={template.id}
                  className={`bg-white border rounded-lg overflow-hidden transition-shadow hover:shadow-md ${
                    template.isCustomized ? 'border-primary-200 ring-1 ring-primary-100' : 'border-gray-200'
                  }`}
                >
                  {/* Main Row */}
                  <div className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Category Icon */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${catConfig.color}`}>
                        {getCategoryIcon(template.category)}
                      </div>

                      {/* Template Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {template.displayName}
                          </h3>
                          {getTierBadge(template.requiredTier)}
                        </div>
                        
                        <p className="text-sm text-gray-500 mb-2 line-clamp-1">
                          {template.description}
                        </p>

                        {/* Status Row */}
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Active/Inactive Badge */}
                          <button
                            onClick={() => canCustomize && handleToggleActive(template)}
                            disabled={!canCustomize}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                              template.isActive
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            } ${!canCustomize ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
                          >
                            {template.isActive ? (
                              <>
                                <CheckCircle2 size={12} />
                                Active
                              </>
                            ) : (
                              <>
                                <X size={12} />
                                Disabled
                              </>
                            )}
                          </button>

                          {/* Category Badge */}
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${catConfig.color}`}>
                            {template.category || 'Other'}
                          </span>

                          {/* Tier Access Warning */}
                          {!template.isAccessible && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                              <Lock size={12} />
                              Requires {template.requiredTier}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Preview Button */}
                        <button
                          onClick={() => handlePreview(template)}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Preview"
                        >
                          <Eye size={18} />
                        </button>

                        {/* Edit/Customize Button */}
                        {canCustomize && template.isAccessible && (
                          template.isCustomized ? (
                            <button
                              onClick={() => handleEdit(template)}
                              className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit3 size={18} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleCustomize(template)}
                              className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title="Customize"
                            >
                              <Copy size={18} />
                            </button>
                          )
                        )}

                        {/* Reset Button (only for customized) */}
                        {canCustomize && template.isCustomized && (
                          <button
                            onClick={() => handleResetTemplate(template)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Reset to Default"
                          >
                            <RotateCcw size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Subject Line Preview */}
                  <div className={`px-4 py-2 border-t text-sm ${
                    template.isCustomized ? 'bg-primary-50/50 border-primary-100' : 'bg-gray-50 border-gray-100'
                  }`}>
                    <span className="text-gray-500">Subject: </span>
                    <span className="text-gray-700">{template.subject}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Help Link */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/help/email-templates')}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            Need help with email templates? View the guide →
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setSelectedTemplate(null);
          setPreviewData(null);
        }}
        title={`Preview: ${selectedTemplate?.displayName || 'Email'}`}
        size="lg"
      >
        {previewData && (
          <div className="space-y-4">
            {/* Subject */}
            <div>
              <label className="text-sm font-medium text-gray-500">Subject</label>
              <p className="mt-1 text-gray-900 font-medium">{previewData.subject}</p>
            </div>

            {/* Email Preview */}
            <div>
              <label className="text-sm font-medium text-gray-500">Email Body</label>
              <div 
                className="mt-2 border border-gray-200 rounded-lg p-4 bg-white max-h-96 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: previewData.htmlBody }}
              />
            </div>

            {/* Sample Data Used */}
            {previewData.sampleData && Object.keys(previewData.sampleData).length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-500">Sample Data Used</label>
                <div className="mt-2 bg-gray-50 rounded-lg p-3 text-xs font-mono space-y-1">
                  {Object.entries(previewData.sampleData).slice(0, 8).map(([key, value]) => (
                    <div key={key} className="flex">
                      <span className="text-purple-600 w-40 flex-shrink-0">{`{{${key}}}`}</span>
                      <span className="text-gray-600">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 border-t flex justify-end">
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedTemplate(null);
                  setPreviewData(null);
                }}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTemplate(null);
        }}
        title={selectedTemplate?.isCustomized ? 'Edit Template' : 'Customize Template'}
        size="lg"
      >
        <div className="space-y-4">
          {/* Info Banner */}
          {!selectedTemplate?.isCustomized && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Sparkles className="text-blue-600 mt-0.5" size={16} />
                <div className="text-sm text-blue-800">
                  <strong>Creating Your Custom Version</strong>
                  <p className="mt-0.5">
                    This will create a copy of the system template that you can edit. 
                    Your custom version will be used instead of the default.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Active Toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <label className="font-medium text-gray-900">Template Active</label>
              <p className="text-sm text-gray-500">When disabled, this email won't be sent</p>
            </div>
            <button
              onClick={() => setEditForm({ ...editForm, isActive: !editForm.isActive })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                editForm.isActive ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  editForm.isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Subject */}
          <div>
            <label className="label">Subject Line</label>
            <input
              type="text"
              value={editForm.subject}
              onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
              className="input"
              placeholder="Email subject..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Use merge fields like {'{{CustomerName}}'} or {'{{BusinessName}}'}
            </p>
          </div>

          {/* HTML Body */}
          <div>
            <label className="label">Email Body (HTML)</label>
            <textarea
              value={editForm.htmlBody}
              onChange={(e) => setEditForm({ ...editForm, htmlBody: e.target.value })}
              className="input font-mono text-sm"
              rows={12}
              placeholder="<p>Your email content...</p>"
            />
            <p className="text-xs text-gray-500 mt-1">
              HTML supported. Common merge fields: {'{{CustomerFirstName}}'}, {'{{JobDate}}'}, {'{{BusinessPhone}}'}
            </p>
          </div>

          {/* Actions */}
          <div className="pt-4 border-t flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={() => {
                setShowEditModal(false);
                setSelectedTemplate(null);
              }}
              className="btn btn-secondary flex-1"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveTemplate}
              disabled={saving}
              className="btn btn-primary flex-1"
            >
              {saving ? 'Saving...' : selectedTemplate?.isCustomized ? 'Save Changes' : 'Create Custom Template'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText || (confirmModal.variant === 'danger' ? 'Delete' : 'OK')}
      />
    </div>
  );
};

export default EmailTemplatesPage;